import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import { Accordion } from '@components/Clinicians/Accordion';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { isBlank } from '@helpers/htmlUtils';
import { confirmationModal } from 'vl-common/src/components/Modals';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';

const IntClinicalNotesFormItem = () => {
  const { t } = useTranslation('common');

  const {
    appointment: { read_only: readOnly, appointment_completed: isCompleted }
  } = useSessionAppointment();

  const appointment_id = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);

  const isV2Appointment = detailsCurrentAppointment?.appt_version === 2;

  const handleNoteChange = useSessionNoteChangeHandler({ field: 'INT' });

  const [fieldValue, setFieldValue] = useSessionFormState('INT');
  const [isCheckedManually, setIsCheckedManually] = useState(false);

  const isEmptyFieldValue = Boolean(isBlank(fieldValue));

  const handleToggleAccordion = (checked: boolean) => {
    if (checked) {
      setIsCheckedManually(true);
      return;
    }

    if (isEmptyFieldValue) {
      setIsCheckedManually(false);
      return;
    }

    confirmationModal({
      title: 'Are you sure?',
      content: 'Your internal clinical notes will be deleted',
      closable: true,
      onOk: () => {
        setFieldValue(undefined);
        setIsCheckedManually(false);
      }
    });
  };

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('INT');

  const isExpanded = isCheckedManually || !isEmptyFieldValue;

  return (
    <Accordion
      isCollapsibleByCheckbox={isV2Appointment}
      key="int-form-item"
      field="int"
      disabled={isCompleted || readOnly}
      label={getNoteLabel('INT')}
      required={isRequired}
      {...(isV2Appointment && { expanded: isExpanded, onChange: handleToggleAccordion })}
    >
      <NoteEditor
        data-testid="clinical-int"
        field="INT"
        ariaLabel={getNoteLabel('INT')}
        placeholder={t('clinicalINTPlaceholder')}
        readOnly={readOnly || isCompleted}
        onChange={handleNoteChange}
      />
    </Accordion>
  );
};

export default IntClinicalNotesFormItem;
